import React, { useState, useMemo } from 'react';
import { X, Printer } from 'lucide-react';

const PrintInventoryModal = ({ 
  isOpen, 
  onClose, 
  items = [],
  currentLocation = 'all'
}) => {
  const [filterLocation, setFilterLocation] = useState(currentLocation);

  const filteredItems = useMemo(() => {
    let filtered = items;
    
    if (filterLocation !== 'all') {
      filtered = items.filter(item => item.ubicacion === filterLocation);
    }
    
    return filtered.sort((a, b) => a.id - b.id);
  }, [items, filterLocation]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const getFilterLabel = () => {
    switch (filterLocation) {
      case 'Campo':
        return 'Campo (Huerta)';
      case 'Warehouse USA':
        return 'Warehouse USA';
      default:
        return 'Todas las ubicaciones';
    }
  };

  const getColumnHeader = () => {
    if (filterLocation === 'Campo') {
      return 'Planificado';
    } else if (filterLocation === 'Warehouse USA') {
      return 'En Tránsito';
    } else {
      return 'En Tránsito / Planificado';
    }
  };

  const getTransitPlannedValue = (item) => {
    if (item.ubicacion === 'Campo') {
      return item.cantidad_planificada || 0;
    } else if (item.ubicacion === 'Warehouse USA') {
      return item.cantidad_en_transito || 0;
    }
    return '—';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content print-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header no-print">
            <h2 className="modal-title">Imprimir Listado de Inventario</h2>
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="filter-section no-print">
            <label className="filter-label">Ubicación:</label>
            <div className="filter-buttons">
              <button
                className={`filter-button ${filterLocation === 'all' ? 'active' : ''}`}
                onClick={() => setFilterLocation('all')}
              >
                Todas
              </button>
              <button
                className={`filter-button ${filterLocation === 'Campo' ? 'active' : ''}`}
                onClick={() => setFilterLocation('Campo')}
              >
                Campo
              </button>
              <button
                className={`filter-button ${filterLocation === 'Warehouse USA' ? 'active' : ''}`}
                onClick={() => setFilterLocation('Warehouse USA')}
              >
                Warehouse USA
              </button>
            </div>
          </div>

          <div className="print-preview">
            <div className="print-header">
              <h1 className="print-company">Patagonia Vital</h1>
              <h2 className="print-title">Listado de Inventario</h2>
              <div className="print-info">
                <p>Ubicación: {getFilterLabel()}</p>
                <p className="print-date">Fecha: {new Date().toLocaleDateString('es-CL')}</p>
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <div className="empty-message">
                <p>No hay productos para imprimir</p>
              </div>
            ) : (
              <table className="print-table">
                <thead>
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-quantity">Cantidad</th>
                    <th className="th-transit">{getColumnHeader()}</th>
                    <th className="th-location">Ubicación</th>
                    <th className="th-date">Última actualización</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td className="td-product">
                        <div className="product-info">
                          <div className="product-name">{item.productos?.nombre || '—'}</div>
                          <div className="product-code">{item.productos?.code || '—'}</div>
                        </div>
                      </td>
                      <td className="td-quantity">{item.cantidad}</td>
                      <td className="td-transit">{getTransitPlannedValue(item)}</td>
                      <td className="td-location">{item.ubicacion || '—'}</td>
                      <td className="td-date">{formatDate(item.fecha_entrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="modal-footer no-print">
            <button className="btn btn-secondary" onClick={onClose}>
              Cerrar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handlePrint}
              disabled={filteredItems.length === 0}
            >
              <Printer size={18} />
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--spacing-4);
        }

        .modal-content {
          background: white;
          border-radius: var(--radius-lg);
          max-width: 1200px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-6);
          border-bottom: 1px solid var(--gray-200);
          background: white;
          position: relative;
          z-index: 1;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin: 0;
        }

        .close-button {
          padding: var(--spacing-2);
          border: none;
          background: transparent;
          color: var(--gray-500);
          cursor: pointer;
          border-radius: var(--radius-md);
          transition: all 0.2s;
        }

        .close-button:hover {
          background-color: var(--gray-100);
          color: var(--gray-700);
        }

        .filter-section {
          padding: var(--spacing-6);
          border-bottom: 1px solid var(--gray-200);
        }

        .filter-label {
          display: block;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: var(--spacing-3);
        }

        .filter-buttons {
          display: flex;
          gap: var(--spacing-3);
        }

        .filter-button {
          flex: 1;
          padding: var(--spacing-3) var(--spacing-4);
          border: 2px solid var(--gray-300);
          background: white;
          color: var(--gray-700);
          font-weight: 600;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-button:hover {
          border-color: var(--primary-blue);
          background-color: rgba(59, 130, 246, 0.05);
        }

        .filter-button.active {
          border-color: var(--primary-blue);
          background-color: var(--primary-blue);
          color: white;
        }

        .print-preview {
          padding: var(--spacing-6);
        }

        .print-header {
          display: none;
          text-align: center;
          margin-bottom: var(--spacing-6);
        }

        .print-company {
          font-size: 24px;
          font-weight: 700;
          color: #2c5f2d;
          margin: 0 0 var(--spacing-2) 0;
        }

        .print-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--gray-900);
          margin: 0 0 var(--spacing-4) 0;
        }

        .print-info {
          font-size: 14px;
          color: var(--gray-600);
        }

        .print-info p {
          margin: var(--spacing-1) 0;
        }

        .print-date {
          font-style: italic;
        }

        .empty-message {
          text-align: center;
          padding: var(--spacing-12);
          color: var(--gray-600);
        }

        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: var(--spacing-4);
        }

        .print-table thead {
          background-color: var(--gray-50);
        }

        .print-table th {
          padding: var(--spacing-3) var(--spacing-4);
          text-align: left;
          font-size: var(--font-size-sm);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid var(--gray-300);
        }

        .print-table td {
          padding: var(--spacing-3) var(--spacing-4);
          border-bottom: 1px solid var(--gray-200);
          color: var(--gray-900);
        }

        .print-table tbody tr:hover {
          background-color: var(--gray-50);
        }

        .th-quantity,
        .th-transit,
        .td-quantity,
        .td-transit {
          text-align: right;
        }

        .th-location,
        .td-location {
          text-align: center;
        }

        .product-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }

        .product-name {
          font-weight: 500;
        }

        .product-code {
          font-family: monospace;
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-3);
          padding: var(--spacing-6);
          border-top: 1px solid var(--gray-200);
        }

        .btn {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-3) var(--spacing-5);
          border: none;
          border-radius: var(--radius-md);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary {
          background-color: var(--gray-100);
          color: var(--gray-700);
        }

        .btn-secondary:hover {
          background-color: var(--gray-200);
        }

        .btn-primary {
          background-color: var(--primary-blue);
          color: white;
        }

        .btn-primary:hover {
          background-color: #2563eb;
        }

        .btn-primary:disabled {
          background-color: var(--gray-300);
          color: var(--gray-500);
          cursor: not-allowed;
        }

        @media print {
          @page {
            margin: 1.5cm;
            size: A4 portrait;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 0;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          body * {
            visibility: hidden;
          }

          .modal-overlay,
          .modal-overlay * {
            visibility: visible;
          }

          .modal-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: auto;
            background: white;
            padding: 0;
            margin: 0;
            overflow: visible;
          }

          .modal-content {
            position: static;
            max-width: none;
            max-height: none;
            box-shadow: none;
            border-radius: 0;
            margin: 0;
            padding: 0;
            overflow: visible;
            height: auto;
          }

          .no-print {
            display: none !important;
          }

          .print-header {
            display: block !important;
            page-break-after: avoid;
            margin-bottom: 20px;
          }

          .print-preview {
            padding: 0;
            overflow: visible;
            height: auto;
          }

          .print-table {
            width: 100%;
            table-layout: fixed;
            border-collapse: collapse;
            margin-top: 0;
          }

          .print-table thead {
            display: table-header-group;
          }

          .print-table tbody {
            display: table-row-group;
          }

          .print-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .print-table th,
          .print-table td {
            border: 1px solid #333;
            padding: 6px 8px;
            font-size: 11px;
            line-height: 1.3;
          }

          .print-table tbody tr:hover {
            background-color: transparent;
          }

          .th-product,
          .td-product {
            width: 30%;
          }

          .th-quantity,
          .td-quantity {
            width: 12%;
          }

          .th-transit,
          .td-transit {
            width: 18%;
          }

          .th-location,
          .td-location {
            width: 15%;
          }

          .th-date,
          .td-date {
            width: 25%;
          }

          .product-info {
            display: block;
          }

          .product-name {
            font-weight: 500;
            margin-bottom: 2px;
          }

          .product-code {
            font-size: 10px;
            color: #666;
          }
        }

        @media (max-width: 768px) {
          .modal-content {
            max-height: 95vh;
          }

          .filter-buttons {
            flex-direction: column;
          }

          .print-table {
            font-size: var(--font-size-sm);
          }

          .print-table th,
          .print-table td {
            padding: var(--spacing-2) var(--spacing-3);
          }
        }
      `}</style>
    </>
  );
};

export default PrintInventoryModal;
